/**
 * @carrier/ocs-client — Zod schemas for REST API request/response validation.
 *
 * Used by apps/api route definitions. Shared here so mcp-server can
 * reference the same shapes if needed.
 */

import { z } from "zod";
import { OCS_HLR_BITRATE_LIMITS } from "./ocs-params.js";

// ---------------------------------------------------------------------------
// OCS provider contract — known-valid v1 methods + response shape schemas.
//
// Ground truth captured via a live-stg smoke against eSIMVault OCS. These pin
// the request/response contract so shape drift (the createAccount /
// getResellerInfo / nested listResellerAccount bugs the smoke found) fails
// loudly at the client boundary instead of being silently mis-read.
//
// Response schemas are deliberately LENIENT: objects `.passthrough()` so the
// provider can add fields without breaking us; we only assert the KEY fields
// and their exact names (the drift that actually bit us).
// ---------------------------------------------------------------------------

/**
 * The valid OCS v1 methods, verified against live-stg — the enumerated set
 * below is authoritative ("the ONLY ones"). This is the ONLY set the server
 * recognizes — notably there is NO `createAccount` or
 * `createSubscriber` (smoke returned OCS error 2 "Unrecognized field"). The
 * client guards against unknown methods using this set so phantom methods are
 * caught at dev time rather than silently round-tripping to a 200+error.
 */
export const OCS_V1_METHODS: ReadonlySet<string> = new Set([
  "getResellerInfo",
  "listResellerAccount",
  "listPrepaidPackageTemplate",
  "listSubscriberPrepaidPackages",
  "listSubscriber",
  "getSingleSubscriber",
  "getSimProviderStatus",
  "getSubscriberLocation",
  "getSubscriberLocationByCellId",
  "esimStatusPerAccount",
  "subscriberUsageOverPeriod",
  "affectRecurringPackageToSubscriber",
  "deleteSubscriberPackage",
  "modifyAccountBalance",
  "modifySubscriberBalance",
  "modifySubscriberContactInfo",
  "modifySubscriberMobilePlan",
  "modifySubscriberSteeringList",
  "modifySubscriberVoipPlan",
  "modifySubscriberPrepaidPackageActivePeriod",
  "modifySubscriberPrepaidPackageLimits",
  "modifySubscriberPrepaidPackageStatus",
  "modifyPPTCore",
  "modifyPPTRecurring",
  "createPrepaidPackageTemplate",
  "createLocationZone",
  "changeNetworkProfileOfLocationZone",
  "hlrGetBitrate",
  "hlrSetBitrate",
  "listSponsor",
  "listDestinationListPrefix",
  "listDetailedLocationZone",
  "listLocationZoneElement",
  "listSubscriberVoipTariff",
  "listVoipTariffRule",
  "affectSubscriberFakePhoneNumber",
  "affectSubscriberRealPhoneNumber",
  "getCustomerTariff",
  "resetSubsGzCounter",
  "sendMtSms",
]);

/**
 * OCS response envelope. Every response is `{ status: { code, msg }, [method]: <data> }`.
 * `status.code !== 0` signals an OCS-level error (HTTP is always 200). The
 * client checks status before this validation runs; this schema exists so
 * callers and tests can assert the envelope shape independently.
 */
export const OcsResponseEnvelope = z
  .object({
    status: z
      .object({
        code: z.number(),
        // Required to match the `OcsStatus` interface in client.ts ({ code, msg }).
        msg: z.string(),
      })
      .passthrough(),
  })
  .passthrough();

// --- getResellerInfo --------------------------------------------------------
// Live shape: { id, name, parentId, parentName, balance, cdrFolder?,
// trafficInfo?, chargingInfo?, contactInfo? }. NOT reseller_id/reseller_name.
export const GetResellerInfoResponse = z
  .object({
    id: z.number(),
    name: z.string(),
    parentId: z.number(),
    parentName: z.string(),
    // Production eSIMVault returns decimal money as a JSON string ("495.52").
    // Staging fixtures used a number, so accept both exact provider variants.
    balance: z.union([z.number(), z.string().regex(/^-?\d+(?:\.\d+)?$/)]),
    cdrFolder: z.string().optional(),
    trafficInfo: z.object({}).passthrough().optional(),
    chargingInfo: z.object({}).passthrough().optional(),
    contactInfo: z.object({}).passthrough().optional(),
  })
  .passthrough();

// --- listResellerAccount ----------------------------------------------------
// Live shape is NESTED: { reseller: [ { id, name, account: [ {…} ] } ] }.
export const ListResellerAccountResponse = z
  .object({
    reseller: z.array(
      z
        .object({
          id: z.number(),
          name: z.string(),
          account: z.array(z.object({}).passthrough()),
        })
        .passthrough(),
    ),
  })
  .passthrough();

// --- listPrepaidPackageTemplate ---------------------------------------------
// Live shape: { template: [ { prepaidpackagetemplateid, prepaidpackagetemplatename,
// resellerid, locationzoneid, … } ] }.
export const ListPrepaidPackageTemplateResponse = z
  .object({
    template: z.array(
      z
        .object({
          prepaidpackagetemplateid: z.number(),
          prepaidpackagetemplatename: z.string(),
          resellerid: z.number(),
          locationzoneid: z.number(),
        })
        .passthrough(),
    ),
  })
  .passthrough();

// --- getSingleSubscriber ----------------------------------------------------
// Live shape: `status` is an ARRAY; status[0].status is a named string
// ("Active"/"Inventory"/"Inactive"/"End of Life"/possibly others). Do NOT
// hardcode a closed enum — assert a non-empty, alpha-leading string.
const SubscriberStatusName = z
  .string()
  .regex(/^[A-Za-z]/, "subscriber status must be a non-empty alpha-leading name");

export const GetSingleSubscriberResponse = z
  .object({
    status: z
      .array(
        z
          .object({
            status: SubscriberStatusName,
          })
          .passthrough(),
      )
      .min(1, "getSingleSubscriber.status must be a non-empty array"),
  })
  .passthrough();

/**
 * Method → response-shape schema. Keyed by OCS method name; only the read
 * methods we depend on are pinned. Methods absent from this map skip shape
 * validation (write/admin mutations return only the status envelope).
 */
export const RESPONSE_SCHEMAS: Record<string, z.ZodType> = {
  getResellerInfo: GetResellerInfoResponse,
  listResellerAccount: ListResellerAccountResponse,
  listPrepaidPackageTemplate: ListPrepaidPackageTemplateResponse,
  getSingleSubscriber: GetSingleSubscriberResponse,
};

// ---------------------------------------------------------------------------
// Common
// ---------------------------------------------------------------------------

export const IccidParam = z.object({ iccid: z.string().min(1) });
export const AccountIdParam = z.object({ accountId: z.coerce.number().int().positive() });
export const PackageIdParam = z.object({ packageId: z.coerce.number().int().positive() });
export const TemplateIdParam = z.object({ templateId: z.coerce.number().int().positive() });
export const ResellerIdParam = z.object({ resellerId: z.coerce.number().int().positive() });

export const DateRangeBody = z.object({
  start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "must be YYYY-MM-DD"),
  end: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "must be YYYY-MM-DD"),
});

export const ErrorResponse = z.object({
  error: z.string(),
  code: z.number().optional(),
  retryAfter: z.number().optional(),
});

export const OkResponse = z.object({
  ok: z.boolean(),
  data: z.unknown().optional(),
});

// ---------------------------------------------------------------------------
// Subscriber
// ---------------------------------------------------------------------------

export const ModifySubscriberStatusBody = z.object({
  newStatus: z.enum(["active", "suspended", "deactivated"]),
});

export const ModifySubscriberBalanceBody = z.object({
  amount: z.number().optional(),
  setBalance: z.number().optional(),
}).refine((v) => v.amount !== undefined || v.setBalance !== undefined, {
  message: "Provide either amount (delta) or setBalance (absolute)",
});

export const ModifySubscriberContactInfoBody = z.object({
  name: z.string().optional(),
  mail: z.string().email().optional(),
  phone: z.string().optional(),
});

export const SetTrafficRestrictionsBody = z.object({
  blockData: z.boolean().optional(),
  blockVoice: z.boolean().optional(),
  blockSms: z.boolean().optional(),
  blockRoaming: z.boolean().optional(),
});

export const ModifySteeringListBody = z.object({
  steeringListId: z.number().int().positive(),
});

export const HlrSetBitrateBody = z.object({
  limit: z.enum(OCS_HLR_BITRATE_LIMITS),
});

// ---------------------------------------------------------------------------
// Packages
// ---------------------------------------------------------------------------

export const AssignPackageBody = z.object({
  packageTemplateId: z.number().int().positive(),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});

export const AssignRecurringPackageBody = z.object({
  packageTemplateId: z.number().int().positive(),
});

export const ModifyPackageLimitsBody = z.object({
  dataLimitMb: z.number().positive().optional(),
  voiceLimitMin: z.number().nonnegative().optional(),
  smsLimit: z.number().nonnegative().optional(),
});

export const ModifyPackageExpiryBody = z.object({
  expiryDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export const ModifyPackageStatusBody = z.object({
  status: z.enum(["active", "paused"]),
});

export const StopResumeRecurringBody = z.object({
  action: z.enum(["stop", "resume"]),
});

export const CreatePackageTemplateBody = z.object({
  name: z.string().min(1),
  dataLimitMb: z.number().positive(),
  validityDays: z.number().int().positive(),
  price: z.number().nonnegative().optional(),
  recurring: z.boolean().optional(),
});

export const ModifyTemplateCoreBody = z.object({
  name: z.string().optional(),
  dataLimitMb: z.number().positive().optional(),
  validityDays: z.number().int().positive().optional(),
});

export const ModifyTemplateRecurringBody = z.object({
  billingCycleDays: z.number().int().positive().optional(),
  autoRenew: z.boolean().optional(),
});

export const ModifyTemplateThrottlingBody = z.object({
  throttleSpeedKbps: z.number().int().nonnegative(),
});

// ---------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------

export const ModifyAccountBalanceBody = z.object({
  amount: z.number(),
  // adapt and add are a signed delta. set replaces the balance. subtract
  // negates a positive amount. The API drops mode before the OCS call.
  mode: z.enum(["adapt", "set", "add", "subtract"]).default("adapt"),
});

export const MoveSubscribersBody = z.object({
  fromAccountId: z.number().int().positive(),
  iccidStart: z.string().min(1),
  iccidEnd: z.string().min(1),
});

// ---------------------------------------------------------------------------
// Location zones
// ---------------------------------------------------------------------------

export const CreateLocationZoneBody = z.object({
  name: z.string().min(1),
  countries: z.array(z.string().length(2)).min(1),
});

// ---------------------------------------------------------------------------
// SMS
// ---------------------------------------------------------------------------

export const SendSmsBody = z.object({
  text: z.string().min(1).max(160),
  senderId: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Change SIM status
// ---------------------------------------------------------------------------

export const ChangeSimStatusBody = z.object({
  newStatus: z.enum(["Enable", "Disable"]),
});

// ---------------------------------------------------------------------------
// Intelligence composites
// ---------------------------------------------------------------------------

export const DiagnoseSubscriberBody = z.object({
  iccid: z.string().min(1),
});

export const FleetHealthQuery = z.object({
  accountId: z.coerce.number().int().positive().optional(),
});

export const ChurnRiskQuery = z.object({
  daysSinceUsage: z.coerce.number().int().positive().default(14),
  limit: z.coerce.number().int().positive().max(500).default(50),
});

export const DetectUsageAnomaliesBody = z.object({
  start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  end: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  thresholdMultiplier: z.number().positive().default(2.5),
});

export const OptimizePackageBody = z.object({
  iccid: z.string().min(1),
});

export const AuditNetworkCoverageBody = z.object({
  locationZoneId: z.number().int().positive().optional(),
});

export const MarketingIntelligenceQuery = z.object({
  segment: z.enum(["low_usage", "high_usage", "expiring_soon", "churned"]).optional(),
});

export const HighCostSubscribersQuery = z.object({
  limit: z.coerce.number().int().positive().max(200).default(20),
  start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  end: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});

// ---------------------------------------------------------------------------
// Webhooks
// ---------------------------------------------------------------------------

export const WebhookEventBody = z.record(z.string(), z.unknown());

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------

export const HealthResponse = z.object({
  ok: z.literal(true),
  version: z.string(),
  build_sha: z.string(),
});
